import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { clearStoredToken } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);

  const [onboardingCheck, setOnboardingCheck] = useState({ checked: false, needs: false });

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setIsAuthenticated(true);
      // Onboarding gate: new users get redirected to the wizard on any
      // ProtectedRoute except /onboarding itself (avoids infinite loop) +
      // Stripe /payments/success (post-checkout return, doesn't need wizard).
      const skipOnboardingPaths = ['/onboarding', '/payments/success', '/payments/cancel'];
      if (!skipOnboardingPaths.includes(location.pathname)) {
        try {
          const st = await axios.get(`${BACKEND_URL}/api/onboarding/status`, { withCredentials: true });
          setOnboardingCheck({ checked: true, needs: st.data.should_show === true });
        } catch { setOnboardingCheck({ checked: true, needs: false }); }
      } else {
        setOnboardingCheck({ checked: true, needs: false });
      }
      // Best-effort referral attribution. If the viewer landed via a
      // /b/{token} share-card CTA, we stashed `fh_referral_token` in
      // localStorage. Fire-and-forget POST — backend is idempotent and
      // silently rejects self-referrals / already-claimed users.
      try {
        const refToken = localStorage.getItem('fh_referral_token');
        if (refToken) {
          axios.post(
            `${BACKEND_URL}/api/share/${refToken}/claim`,
            {},
            { withCredentials: true },
          )
            .catch(() => { /* invalid / expired — silent */ })
            .finally(() => localStorage.removeItem('fh_referral_token'));
        }
      } catch { /* private mode / quota — non-blocking */ }
      // Best-effort affiliate attribution: if the landing page stashed
      // `fh_affiliate_ref` from a `?ref=` URL param, claim it now.
      // Backend is idempotent, no-ops on self-referral / already-claimed.
      try {
        const affRef = localStorage.getItem('fh_affiliate_ref');
        if (affRef) {
          axios.post(
            `${BACKEND_URL}/api/affiliates/claim`,
            { code: affRef },
            { withCredentials: true },
          )
            .catch(() => { /* invalid code / network — silent, we clean up in finally */ })
            .finally(() => localStorage.removeItem('fh_affiliate_ref'));
        }
      } catch { /* non-blocking */ }
    } catch (err) {
      // Token inválido/expirado no localStorage (ex.: importado de outro browser)
      // envenena o interceptor Bearer — limpar para o próximo login partir limpo.
      if (err?.response?.status === 401) {
        clearStoredToken();
      }
      setIsAuthenticated(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.user) {
      setOnboardingCheck({ checked: true, needs: false });
      return;
    }
    checkAuth();
  }, [location.state?.user, checkAuth]);

  if (isAuthenticated === null || (isAuthenticated && !onboardingCheck.checked)) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (onboardingCheck.needs) {
    return <Navigate to="/onboarding" replace />;
  }

  return React.cloneElement(children, { user });
};

export default ProtectedRoute;